import React from 'react';
import { Box, IconButton } from '@mui/material';
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  CallEnd,
  Fullscreen,
  FullscreenExit,
  ScreenShare,
  StopScreenShare,
  ClosedCaption,
  ClosedCaptionDisabled,
} from '@mui/icons-material';

/**
 * Video Controls Bar Component
 * Contains all control buttons for the video call
 */
function VideoControls({
  isCallActive,
  isAudioEnabled,
  isVideoEnabled,
  isScreenSharing,
  isRemoteScreenSharing,
  isFullscreen,
  isCaptionsEnabled,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onEndCall,
  onToggleFullscreen,
  onToggleCaptions,
}) {

  const handleAudioClick = () => {
    console.log('🎤 Audio button clicked!');
    if (onToggleAudio) onToggleAudio();
  };

  const handleVideoClick = () => {
    console.log('📹 Video button clicked!');
    if (onToggleVideo) onToggleVideo();
  };

  const handleScreenShareClick = () => {
    console.log('🖥️ Screen share button clicked!');
    if (onToggleScreenShare) onToggleScreenShare();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        py: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3 },
        zIndex: 9999,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        pointerEvents: 'auto',
      }}
    >
      {/* Audio Toggle */}
      <IconButton
        onClick={handleAudioClick}
        disabled={!isCallActive}
        sx={{
          backgroundColor: isAudioEnabled ? 'rgba(255, 255, 255, 0.1)' : 'error.main',
          color: 'white',
          width: { xs: 44, sm: 48 },
          height: { xs: 44, sm: 48 },
          cursor: 'pointer',
          pointerEvents: 'auto',
          '&:hover': {
            backgroundColor: isAudioEnabled ? 'rgba(255, 255, 255, 0.2)' : 'error.dark',
          },
          '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'rgba(255, 255, 255, 0.3)',
            cursor: 'not-allowed',
          },
        }}
      >
        {isAudioEnabled ? <Mic /> : <MicOff />}
      </IconButton>

      {/* Video Toggle */}
      <IconButton
        onClick={handleVideoClick}
        disabled={!isCallActive || isScreenSharing}
        sx={{
          backgroundColor: isVideoEnabled ? 'rgba(255, 255, 255, 0.1)' : 'error.main',
          color: 'white',
          width: { xs: 44, sm: 48 },
          height: { xs: 44, sm: 48 },
          cursor: 'pointer',
          pointerEvents: 'auto',
          '&:hover': {
            backgroundColor: isVideoEnabled ? 'rgba(255, 255, 255, 0.2)' : 'error.dark',
          },
          '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'rgba(255, 255, 255, 0.3)',
            cursor: 'not-allowed',
          },
          opacity: isScreenSharing ? 0.5 : 1,
        }}
      >
        {isVideoEnabled ? <Videocam /> : <VideocamOff />}
      </IconButton>

      {/* Screen Share Toggle */}
      <IconButton
        onClick={handleScreenShareClick}
        disabled={!isCallActive}
        sx={{
          backgroundColor: isScreenSharing ? 'primary.main' : 
                         isRemoteScreenSharing ? 'warning.main' : 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          width: { xs: 44, sm: 48 },
          height: { xs: 44, sm: 48 },
          cursor: 'pointer',
          pointerEvents: 'auto',
          '&:hover': {
            backgroundColor: isScreenSharing ? 'primary.dark' : 
                           isRemoteScreenSharing ? 'warning.dark' : 'rgba(255, 255, 255, 0.2)',
          },
          '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'rgba(255, 255, 255, 0.3)',
            cursor: 'not-allowed',
          },
          position: 'relative',
        }}
        title={isScreenSharing ? 'Stop sharing screen' : 
               isRemoteScreenSharing ? 'Other participant is sharing screen' : 
               'Share your screen'}
      >
        {isScreenSharing ? <StopScreenShare /> : <ScreenShare />}
        
        {/* Remote screen share indicator */}
        {isRemoteScreenSharing && !isScreenSharing && (
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              right: -2,
              width: 12,
              height: 12,
              backgroundColor: 'warning.main',
              borderRadius: '50%',
              border: '2px solid white',
            }}
          />
        )}
      </IconButton>

      {/* End Call */}
      <IconButton
        onClick={onEndCall}
        sx={{
          backgroundColor: 'error.main',
          color: 'white',
          mx: { xs: 0.5, sm: 2 },
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          '&:hover': {
            backgroundColor: 'error.dark',
            transform: 'scale(1.05)',
          },
          transition: 'all 0.2s',
        }}
      >
        <CallEnd />
      </IconButton>

      {/* Live Captions Toggle */}
      {/* <IconButton
        onClick={onToggleCaptions}
        disabled={!isCallActive}
        sx={{
          backgroundColor: isCaptionsEnabled ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          width: { xs: 44, sm: 48 },
          height: { xs: 44, sm: 48 },
          cursor: 'pointer',
          pointerEvents: 'auto',
          '&:hover': {
            backgroundColor: isCaptionsEnabled ? 'primary.dark' : 'rgba(255, 255, 255, 0.2)',
          },
          '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: 'rgba(255, 255, 255, 0.3)',
            cursor: 'not-allowed',
          },
        }}
        title={isCaptionsEnabled ? 'Hide captions' : 'Show live captions'}
      >
        {isCaptionsEnabled ? <ClosedCaption /> : <ClosedCaptionDisabled />}
      </IconButton> */}

      {/* Fullscreen Toggle */}
      <IconButton
        onClick={onToggleFullscreen}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          width: { xs: 44, sm: 48 },
          height: { xs: 44, sm: 48 },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
      >
        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
      </IconButton>
    </Box>
  );
}

export default VideoControls;
